import { ReactNode } from "react"
import { ReactKeycloakProvider } from "@react-keycloak/web"
import { getKeycloakInstance } from "@/config/keycloak"
import { Spinner } from "@/components/ui/spinner"

// Get singleton Keycloak instance outside component
// This ensures it persists across React StrictMode remounts
const keycloak = getKeycloakInstance()

interface KeycloakProviderProps {
  children: ReactNode
}

/**
 * KeycloakProvider Component
 *
 * Wraps the application with Keycloak authentication context.
 * Handles initialization, token management, and authentication state.
 * Uses a singleton Keycloak instance to prevent re-initialization errors.
 *
 * @param children - Child components to be wrapped with Keycloak context
 */
export function KeycloakProvider({ children }: KeycloakProviderProps) {
  /**
   * Keycloak initialization configuration
   * - onLoad: 'login-required' ensures users must authenticate before accessing the app
   * - checkLoginIframe: false disables iframe checking for better compatibility
   */
  const initOptions = {
    onLoad: "login-required" as const,
    checkLoginIframe: false,
  }

  /**
   * Loading component displayed during Keycloak initialization
   */
  const loadingComponent = (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Spinner className="mx-auto mb-4" />
        <p className="text-muted-foreground">Initializing authentication...</p>
      </div>
    </div>
  )

  /**
   * Callback when Keycloak authentication event occurs
   * Useful for debugging and logging
   */
  const onEvent = (event: string) => {
    console.log("Keycloak event:", event)
  }

  /**
   * Callback when Keycloak tokens are updated
   * Automatically handles token refresh
   */
  const onTokens = (tokens: unknown) => {
    console.log("Keycloak tokens updated:", tokens)
  }

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={initOptions}
      LoadingComponent={loadingComponent}
      onEvent={onEvent}
      onTokens={onTokens}
    >
      {children}
    </ReactKeycloakProvider>
  )
}

